import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { BASE_URL } from '../../constants/config';
import { filterTestPractitioners } from '../../utils/practitioners';

// Fisher-Yates shuffle — returns a new array, does not mutate input.
// Applied on every fetch so homepage/team carousels don't always lead with
// the same practitioners.
const shuffleArray = (arr) => {
  if (!Array.isArray(arr) || arr.length <= 1) return arr || [];
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

export const fetchPractitioners = createAsyncThunk(
  'practitioners/fetch',
  async (options = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/practitioners`);
      // Backend returns the approved practitioner array directly.
      return shuffleArray(response?.data);
    } catch (error) {
      console.error('Error fetching practitioners:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    condition: (options = {}, { getState }) => {
      const { practitioners } = getState();
      if (options.force) return true;
      if (practitioners.data?.length > 0 || practitioners.loading) return false;
      return true;
    },
  }
);

const practitionersSlice = createSlice({
  name: 'practitioners',
  initialState: {
    data: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPractitioners.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPractitioners.fulfilled, (state, action) => {
        state.loading = false;
        state.data = filterTestPractitioners(action.payload);
      })
      .addCase(fetchPractitioners.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Selectors
export const selectPractitioners = (state) => state.practitioners.data;
export const selectPractitionersLoading = (state) => state.practitioners.loading;

const slugify = (s) => (s || '').toString().toLowerCase().trim().replaceAll(/\s+/g, '-');

/**
 * Find a practitioner by the URL name slug (e.g. "luis-miguel-gallardo").
 * Null-safe against missing name parts.
 */
export const selectPractitionerByUrlName = (urlName) => (state) => {
  if (!urlName) return null;
  const practitioners = state.practitioners.data || [];
  return (
    practitioners.find((p) => {
      const first = p.firstNameLowerCase || p.firstName;
      const last = p.lastNameLowerCase || p.lastName;
      if (first && last && `${slugify(first)}-${slugify(last)}` === urlName) return true;
      if (p.name && slugify(p.name) === urlName) return true;
      return false;
    }) || null
  );
};

export default practitionersSlice.reducer;
